import React from "react";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import styled from "@mui/material/styles/styled";

import FreeTrialAlert from "@app/analytics/account/components/FreeTrialAlert";
import useApps from "@app/common/hooks/useApps";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import { usePageTitle } from "@app/utils/hooks";

import Stack from "@rui/Stack";

import BillingHistory from "./BillingHistory";
import BillingUpdateStatusBanner from "./BillingUpdateStatusBanner";
import CurrentPlan from "./CurrentPlan";
import EmptySeatsAlert from "./EmptySeatsAlert";
import LicenseMaximumAlert from "./LicenseMaximumAlert";
import LicenseUsage from "./LicenseUsage";
import PaymentMethod from "./PaymentMethod";
import VideoUploadCredits from "./VideoUploadCredits";

const GridContainer = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    "& .MuiGrid-grid-xs-12": {
      // at really small widths some of the grid items don't expand to fit the
      // width of the page if the content of the card doesn't force it to, so
      // ensure all the cards are the same width regardless of the content
      minWidth: "100%",
    },
  },
}));

const BillingManage = () => {
  usePageTitle("Billing - Manage", "Plan & Billing");

  const { canEditBillingPlan } = useFeatureAccess();
  const { client } = useApps();

  // the only time we don't want to show the license usage module is if the current user
  // is not authorized to update the billing account. in any other case, even for free
  // plans, we still want to allow them to manage their licenses (could want to purchase
  // licenses in preparation for creating a workspace, or could want to remove licenses after
  // deleting a workspace)
  const showLicensesUsed = canEditBillingPlan;

  return (
    <Container maxWidth="xl" sx={{ pb: 4 }}>
      <Stack px={1} py={2} spacing={1.5}>
        <Stack spacing={1}>
          <BillingUpdateStatusBanner />
          <FreeTrialAlert />
          <EmptySeatsAlert />
          <LicenseMaximumAlert />
        </Stack>
        <GridContainer container spacing={2}>
          <Grid item xs={12} lg={6}>
            <CurrentPlan client={client} />
          </Grid>
          {showLicensesUsed && (
            <Grid item xs={12} lg={6}>
              <LicenseUsage />
            </Grid>
          )}
          <Grid item xs={12} lg={6}>
            <VideoUploadCredits canPurchase={canEditBillingPlan} />
          </Grid>
          {canEditBillingPlan && client !== "zoom" && (
            <>
              <Grid item xs={12} lg={6}>
                <PaymentMethod />
              </Grid>
              <Grid item xs={12}>
                <BillingHistory />
              </Grid>
            </>
          )}
        </GridContainer>
      </Stack>
    </Container>
  );
};

export default BillingManage;
